import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ErrorApi } from '../lib/api';
import { useGuardarUsuario, useUsuario } from '../lib/usuarios';
import { useMe, usePuede } from '../lib/auth';
import { useMigas } from '../lib/migas';
import { Contenedor } from '../componentes/Contenedor';
import { BotonVolver } from '../componentes/BotonVolver';
import { Boton } from '../componentes/Boton';
import { FormularioUsuario } from '../componentes/FormularioUsuario';
import { EnlaceDeInvitacion } from '../componentes/EnlaceDeInvitacion';
import { ETIQUETA_ESTADO, fecha } from './Usuarios';
import { Escudo, Reloj, Usuario as IconoUsuario } from '../componentes/iconos';

/**
 * Perfil de una cuenta.
 *
 * Acá viven las acciones que salieron de la tabla —editar, archivar, reenviar la
 * invitación—: una fila de listado presenta, no opera, y los botones por fila eran
 * además lo que desalineaba las columnas.
 *
 * ── ⚠️ Lo que NUNCA va acá ──
 * **Las respuestas de la persona.** Eso es la frontera 3 (D-009): tiene su propio
 * permiso y su propia pantalla. Un perfil que las mostrara sería la puerta lateral
 * más cómoda a lo que el producto promete no enseñar, y nadie lo notaría hasta que
 * un cliente reclame.
 */
export function UsuarioPerfil() {
  const { id } = useParams<{ id: string }>();
  const usuarioId = Number(id);
  const valido = Number.isInteger(usuarioId) && usuarioId > 0;

  const consulta = useUsuario(valido ? usuarioId : null);
  const u = consulta.data;

  useMigas([{ texto: 'Usuarios', a: '/usuarios' }, { texto: u?.nombre ?? 'Cuenta' }]);

  const { usuario: yo } = useMe();
  const puede = usePuede('usuarios.administrar');
  const { editar, reinvitar } = useGuardarUsuario();

  const [editando, setEditando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enlace, setEnlace] = useState<string | null>(null);

  async function accion(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No pudimos completar la operación.');
    }
  }

  if (!valido || (consulta.isError && !u)) {
    return (
      <Contenedor>
        <div className="rounded-2xl border border-marino-100 bg-white py-16 text-center">
          <h1 className="font-semibold text-marino-700">No encontramos esa cuenta</h1>
          <p className="mt-1 text-sm text-marino-400">
            Puede que la hayan quitado, o que la dirección esté mal escrita.
          </p>
          <div className="mt-5 flex justify-center">
            <BotonVolver a="/usuarios">Volver a Usuarios</BotonVolver>
          </div>
        </div>
      </Contenedor>
    );
  }

  const esMiCuenta = u?.id === yo?.id;

  return (
    <Contenedor>
      <BotonVolver a="/usuarios">Usuarios</BotonVolver>

      {consulta.isPending && <p className="py-16 text-center text-marino-400">Cargando…</p>}

      {error && (
        <div
          role="alert"
          className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      {enlace && (
        <div className="mt-4">
          <EnlaceDeInvitacion enlace={enlace} nombre={u?.nombre} alCerrar={() => setEnlace(null)} />
        </div>
      )}

      {u && (
        <>
          <header className="mt-4 flex flex-wrap items-start gap-4">
            <span
              aria-hidden="true"
              className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-marino-50 text-marino-500"
            >
              <IconoUsuario size={28} />
            </span>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="text-2xl font-bold text-marino-800">{u.nombre}</h1>
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    ETIQUETA_ESTADO[u.estado]?.clases ?? 'bg-marino-50 text-marino-500'
                  }`}
                >
                  {ETIQUETA_ESTADO[u.estado]?.texto ?? u.estado}
                </span>
                {esMiCuenta && (
                  <span className="rounded-full bg-marino-50 px-2.5 py-0.5 text-xs font-semibold text-marino-500">
                    Tú
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-marino-400">{u.correo}</p>
            </div>

            {puede && (
              <Boton variante="secundario" onClick={() => setEditando(true)} className="px-4 py-2.5 text-sm">
                Editar
              </Boton>
            )}
          </header>

          <div className="mt-6 grid gap-5 lg:grid-cols-5">
            <div className="space-y-5 lg:col-span-3">
              <section className="rounded-2xl border border-marino-100 bg-white p-5">
                <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-marino-400">
                  Datos de la cuenta
                </h2>
                <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
                  <Dato titulo="Rol" valor={u.rolNombre} />
                  <Dato
                    titulo="Pertenece a"
                    valor={u.empresaNombre}
                    a={u.empresaId === yo?.empresaId ? undefined : `/clientes/${u.empresaId}`}
                  />
                  <Dato titulo="Cuenta creada" valor={fecha(u.creadoEn)} />
                  <Dato
                    titulo="Último acceso"
                    valor={u.ultimoAccesoEn ? fecha(u.ultimoAccesoEn) : 'Nunca entró'}
                  />
                </dl>

                {/*
                  Se dice qué NO se ve, y por qué. Sin esta línea, alguien va a
                  pedir «que el perfil muestre lo que respondió» sin saber que eso
                  es justamente lo que el producto promete no enseñar.
                */}
                <p className="mt-5 flex items-start gap-2 rounded-xl border border-marino-100 bg-marino-50/60 px-4 py-3 text-xs text-marino-500">
                  <Escudo size={14} className="mt-0.5 shrink-0" />
                  Acá no se ven las respuestas de esta persona. Eso es confidencial y se consulta
                  desde los resultados, que es otra pantalla y otro permiso.
                </p>
              </section>
            </div>

            <div className="space-y-5 lg:col-span-2">
              {u.estado === 'sin_activar' && puede && (
                <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
                  <h2 className="flex items-center gap-2 font-semibold text-amber-900">
                    <Reloj size={17} /> Todavía no ha entrado
                  </h2>
                  <p className="mt-1 text-sm text-amber-800">
                    {u.invitacionPendiente
                      ? 'Tiene una invitación viva. Reenviarla genera un enlace nuevo y anula el anterior.'
                      : 'Su invitación venció. Hay que mandarle una nueva.'}
                  </p>
                  <Boton
                    className="mt-3 px-3.5 py-2 text-sm"
                    cargando={reinvitar.isPending}
                    onClick={() =>
                      void accion(async () => {
                        const r = await reinvitar.mutateAsync(u.id);
                        if (r.enlaceDev) setEnlace(r.enlaceDev);
                      })
                    }
                  >
                    Reenviar invitación
                  </Boton>
                </section>
              )}

              {puede && !esMiCuenta && (
                <section className="rounded-2xl border border-marino-100 bg-white p-5">
                  <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-marino-400">
                    {u.estado === 'desactivado' ? 'Reactivar la cuenta' : 'Archivar la cuenta'}
                  </h2>
                  <p className="text-sm text-marino-500">
                    {u.estado === 'desactivado'
                      ? 'Vuelve a poder entrar. Si nunca activó su cuenta, seguirá pendiente de hacerlo.'
                      : 'Deja de poder entrar y se cierran sus sesiones abiertas. Nada se borra: lo que respondió sigue sosteniendo los resultados.'}
                  </p>
                  <Boton
                    variante="secundario"
                    className="mt-3 px-3.5 py-2 text-sm"
                    cargando={editar.isPending}
                    onClick={() =>
                      void accion(() =>
                        editar.mutateAsync({
                          id: u.id,
                          cambios: { activo: u.estado === 'desactivado' },
                        }),
                      )
                    }
                  >
                    {u.estado === 'desactivado' ? 'Reactivar' : 'Archivar'}
                  </Boton>
                </section>
              )}

              {/*
                Nadie se archiva a sí mismo: sin esto, el único administrador se
                deja fuera con un clic y hay que entrar por la base de datos. El
                servidor lo rechaza con un 409; acá directamente no se ofrece.
              */}
              {esMiCuenta && (
                <p className="rounded-2xl border border-marino-100 bg-marino-50/60 px-4 py-3 text-sm text-marino-500">
                  Es tu propia cuenta: no puedes archivarla ni cambiarte el rol. Pídeselo a otro
                  administrador.
                </p>
              )}
            </div>
          </div>

          <FormularioUsuario
            objetivo={editando ? u : null}
            copathyId={yo?.empresaId ?? null}
            alCerrar={() => setEditando(false)}
            alFallar={setError}
          />
        </>
      )}
    </Contenedor>
  );
}

function Dato({ titulo, valor, a }: { titulo: string; valor: string; a?: string }) {
  return (
    <div className="flex flex-col">
      <dt className="text-marino-400">{titulo}</dt>
      <dd className="font-medium text-marino-800">
        {a ? (
          <Link to={a} className="transition hover:text-naranja-700 hover:underline">
            {valor}
          </Link>
        ) : (
          valor
        )}
      </dd>
    </div>
  );
}
